import api from "@/utils/api";
import { appApiEndpoint } from "@/environment";
import { baseApi } from "./baseApi";
import { flattenTree } from "@/utils/helpers";

// Job Board utility functions
const augmentJobBoard = (jobBoard) => {
  // Create 2-deep list of nested departments, for rendering the job-row
  jobBoard.flatDepartments = jobBoard.departments.map((department) => {
    return {
      ...department,
      flatChildren: flattenTree(
        department.children.map((c) => {
          return {
            ...c,
            parent: department,
          };
        }),
        (root) => {
          return root.children;
        }
      ),
    };
  });

  // Create 2-deep list of nested offices, for rendering the job-row
  jobBoard.flatOffices = jobBoard.offices.map((office) => {
    return {
      ...office,
      flatChildren: flattenTree(
        office.children.map((c) => {
          return {
            ...c,
            parent: office,
          };
        }),
        (root) => {
          return root.children;
        }
      ),
    };
  });

  // Add top-level department and office to each job, for filtering
  jobBoard.jobs = jobBoard.jobs.map((job) => {
    const jobDepartmentPermalinks = job.departments.map((d) => d.permalink);
    const jobOfficePermalinks = job.offices.map((o) => o.permalink);
    return {
      ...job,
      topDepartment: jobBoard.flatDepartments.find((top) => {
        if (jobDepartmentPermalinks.includes(top.permalink)) return top;
        return !!top.flatChildren.find((c) =>
          jobDepartmentPermalinks.includes(c.permalink)
        );
      }),
      topOffice: jobBoard.flatOffices.find((top) => {
        if (jobOfficePermalinks.includes(top.permalink)) return top;
        return !!top.flatChildren.find((c) =>
          jobOfficePermalinks.includes(c.permalink)
        );
      }),
    };
  });

  // Add jobCount to top-level departments and flatChildren
  jobBoard.flatDepartments = jobBoard.flatDepartments.map((department) => {
    const flatChildren = department.flatChildren.map((child) => {
      // Check each department in the departments array
      const jobCount = jobBoard.jobs.filter((j) => {
        return j.departments.find((d) => d.permalink === child.permalink);
      }).length;

      return {
        ...child,
        jobCount,
      };
    });

    return {
      ...department,
      jobCount:
        jobBoard.jobs.filter((j) =>
          j.departments.map((d) => d.permalink).includes(department.permalink)
        ).length + flatChildren.reduce((acc, c) => acc + c.jobCount, 0),
      flatChildren,
    };
  });

  // Add jobCount to top-level offices and flatChildren
  jobBoard.flatOffices = jobBoard.flatOffices.map((office) => {
    const flatChildren = office.flatChildren.map((child) => {
      // Check each office in the offices array
      const jobCount = jobBoard.jobs.filter((j) => {
        return j.offices.find((d) => d.permalink === child.permalink);
      }).length;

      return {
        ...child,
        jobCount,
      };
    });

    return {
      ...office,
      jobCount:
        jobBoard.jobs.filter((j) =>
          j.offices.map((o) => o.permalink).includes(office.permalink)
        ).length + flatChildren.reduce((acc, c) => acc + c.jobCount, 0),
      flatChildren,
    };
  });

  return jobBoard;
};

// Job Boards API slice - extends the base API
export const jobBoardsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get job boards (migrated from createAsyncThunk)
    getJobBoards: builder.query({
      queryFn: async ({
        employerPermalink,
        jobBoardPermalinks,
        includeEuLocations = true,
        jobPageStates = ["public", "private"],
        perPage = 1000,
      }) => {
        try {
          // Build query parameters using URLSearchParams
          const params = new URLSearchParams();

          jobPageStates.forEach((state) => {
            params.append("jobPageStates[]", state);
          });
          params.append("perPage", perPage);
          params.append("includeEuLocations", includeEuLocations);

          // Make a separate call for each job board and return the results
          const jobBoards = await Promise.all(
            jobBoardPermalinks.map((jobBoardPermalink) => {
              const url = new URL(
                `${appApiEndpoint}/v1/employers/${employerPermalink}/job-boards/${jobBoardPermalink}`
              );
              url.search = params.toString();

              return api.get(
                true, // withCredentials
                url.toString()
              );
            })
          );

          return {
            data: jobBoards.map((jobBoard) => augmentJobBoard(jobBoard)),
          };
        } catch (error) {
          return { error };
        }
      },
      providesTags: ["JobBoards"],
    }),

    // Job Board Job Departments
    getJobBoardJobDepartments: builder.query({
      query: ({ employerPermalink, jobBoardPermalink, isPrefetchQuery = false }) => ({
        url: `${appApiEndpoint}/v1/employers/${employerPermalink}/job-boards/${jobBoardPermalink}/job-departments?perPage=1000`,
        method: "GET",
        isPrefetchQuery,
      }),
      providesTags: (result, error, { jobBoardPermalink }) => [
        { type: "JobBoardDepartments", id: jobBoardPermalink },
      ],
    }),

    createJobBoardJobDepartment: builder.mutation({
      query: ({ employerPermalink, jobBoardPermalink, payload }) => ({
        url: `${appApiEndpoint}/v1/employers/${employerPermalink}/job-boards/${jobBoardPermalink}/job-departments`,
        method: "POST",
        data: payload,
      }),
      invalidatesTags: (result, error, { jobBoardPermalink }) => [
        { type: "JobBoardDepartments", id: jobBoardPermalink },
      ],
    }),

    updateJobBoardJobDepartment: builder.mutation({
      query: ({
        employerPermalink,
        jobBoardPermalink,
        jobDepartmentPermalink,
        payload,
      }) => ({
        url: `${appApiEndpoint}/v1/employers/${employerPermalink}/job-boards/${jobBoardPermalink}/job-departments/${jobDepartmentPermalink}`,
        method: "PUT",
        data: payload,
      }),
      invalidatesTags: (result, error, { jobBoardPermalink }) => [
        { type: "JobBoardDepartments", id: jobBoardPermalink },
      ],
    }),

    // Job Board Job Offices
    getJobBoardJobOffices: builder.query({
      query: ({ employerPermalink, jobBoardPermalink, isPrefetchQuery = false }) => ({
        url: `${appApiEndpoint}/v1/employers/${employerPermalink}/job-boards/${jobBoardPermalink}/job-offices?perPage=1000`,
        method: "GET",
        isPrefetchQuery,
      }),
      providesTags: (result, error, { jobBoardPermalink }) => [
        { type: "JobBoardOffices", id: jobBoardPermalink },
      ],
    }),

    createJobBoardJobOffice: builder.mutation({
      query: ({ employerPermalink, jobBoardPermalink, payload }) => ({
        url: `${appApiEndpoint}/v1/employers/${employerPermalink}/job-boards/${jobBoardPermalink}/job-offices`,
        method: "POST",
        data: payload,
      }),
      invalidatesTags: (result, error, { jobBoardPermalink }) => [
        { type: "JobBoardOffices", id: jobBoardPermalink },
      ],
    }),

    updateJobBoardJobOffice: builder.mutation({
      query: ({
        employerPermalink,
        jobBoardPermalink,
        jobOfficePermalink,
        payload,
      }) => ({
        url: `${appApiEndpoint}/v1/employers/${employerPermalink}/job-boards/${jobBoardPermalink}/job-offices/${jobOfficePermalink}`,
        method: "PUT",
        data: payload,
      }),
      invalidatesTags: (result, error, { jobBoardPermalink }) => [
        { type: "JobBoardOffices", id: jobBoardPermalink },
      ],
    }),

    // Get paginated job pages for a job board
    getJobBoardJobPages: builder.query({
      query: ({
        employerPermalink,
        jobBoardPermalink,
        perPage = 50,
        cursor = null,
        search = null,
        office = null,
        department = null,
        jobPageStates = null,
        includeEuLocations = true,
      }) => {
        let url = new URL(
          `${appApiEndpoint}/v1/employers/${employerPermalink}/job-boards/${jobBoardPermalink}`
        );
        let params = new URLSearchParams();
        params.append("perPage", perPage);

        if (cursor) {
          params.append("cursor", cursor);
        }
        if (search) {
          params.append("search", search);
        }

        // Build filters object for department and location
        if (office) {
          params.append("filters[office]", office);
        }
        if (department) {
          params.append("filters[department]", department);
        }

        if (jobPageStates && jobPageStates.length > 0) {
          jobPageStates.forEach((state) => {
            params.append("jobPageStates[]", state);
          });
        }

        params.append("includeEuLocations", includeEuLocations);

        url.search = params.toString();
        return { url: url.toString(), method: "GET" };
      },
      transformResponse: (response) => {
        // Augment the response with additional data while preserving original structure
        return augmentJobBoard(response);
      },
      providesTags: (result, error, { jobBoardPermalink }) => [
        { type: "JobBoardJobPages", id: jobBoardPermalink },
      ],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetJobBoardsQuery,
  useGetJobBoardJobDepartmentsQuery,
  useCreateJobBoardJobDepartmentMutation,
  useUpdateJobBoardJobDepartmentMutation,
  useGetJobBoardJobOfficesQuery,
  useCreateJobBoardJobOfficeMutation,
  useUpdateJobBoardJobOfficeMutation,
  useGetJobBoardJobPagesQuery,
} = jobBoardsApi;
